import { takeEvery, put, select, all } from 'redux-saga/effects';
import { toastr } from 'react-redux-toastr';

import * as actions from '../../actions/admin/users';
import * as types from '../../actions/admin/users/types';
import { setListSettings } from '../../actions/list-settings';

import listNames from '../../constants/list-names';
import httpStatusCodes from '../../constants/http-status-codes';

import { getListSettingsFromQueryParams } from '../../tools';
import apiFetcher from '../../tools/api-fetcher';
import queryStringify from '../../tools/query-stringify';
import handleError from '../../tools/error-helper';

function* getAdminUsers({ queryParams }) {
  yield put(actions.setUsersListLoading(true));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`/admins/users/summaries?${queryStringify({ ...queryParams })}`);

    yield put(actions.setUsersList(data));

    yield put(actions.setQueryParams(queryParams));

    const listSettings = yield getListSettingsFromQueryParams(queryParams);
    
    yield put(setListSettings(listNames.adminUsers, listSettings)); 
  } catch (exception) {
    handleError(exception, 'Failed to load users.');
  }
  
  yield put(actions.setUsersListLoading(false));
}

function* addNewAdmin({ newAdmin }) {
  yield put(actions.setAddingNewAdmin(true));

  try {
    yield apiFetcher.auth().post(`admins/users/administrators`, newAdmin);

    toastr.success('The new administrator was successfully added.');
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.Conflict:
        yield put(
          actions.setNewAdminErrors({ email: exception.response.statusText })
        );
        break;
      default:
        handleError(exception, 'Failed to add new administrator.');
        break;
    }
  }

  yield put(actions.setAddingNewAdmin(false));
}

function* editAdminProfile({ profileData, userId }) {
  const adminUsers = yield select(state => state.adminUsers);
  const queryParams = adminUsers.get('userListQueryParams');

  yield put(actions.setEditingAdminProfile(true));

  try {
    yield apiFetcher
      .auth()
      .put(`admins/users/administrators/${userId}/profile`, profileData);

    yield put(actions.getUsersList(queryParams));

    toastr.success('The administrator`s profile was successfully edited.');
  } catch (exception) {
    handleError(exception, 'Failed to edit administrator`s profile.');
  }

  yield put(actions.setEditingAdminProfile(false));
}

function* changeUserStatus(userId, url, successMessage, errorMessage) {
  const adminUsers = yield select(state => state.adminUsers);
  yield put(actions.setUserStatusUpdating(true));

  try {
    yield apiFetcher.auth().post(url, { userId });
    yield toastr.success(successMessage);

    yield put(actions.getUsersList(adminUsers.get('userListQueryParams')));
  } catch (exception) {
    handleError(exception, errorMessage);
  }

  yield put(actions.setUserStatusUpdating(false));
}

function* enableUser({ userId }) {
  yield changeUserStatus(
    userId,
    '/admins/users/active',
    'User was successfully enabled.',
    'Failed to enable user.'
  );
}

function* disableUser({ userId }) {
  yield changeUserStatus(
    userId,
    '/admins/users/disabled',
    'User was successfully disabled.',
    'Failed to disable user.'
  );
}

export default function* adminUsersSaga() {
  yield all([
    takeEvery(types.ADMIN_USERS_GET_LIST, getAdminUsers),
    takeEvery(types.ADMIN_USERS_ADD_NEW_ADMIN, addNewAdmin),
    takeEvery(types.ADMIN_USERS_EDIT_ADMIN_PROFILE, editAdminProfile),
    takeEvery(types.ADMIN_USERS_DISABLE_USER, disableUser),
    takeEvery(types.ADMIN_USERS_ENABLE_USER, enableUser)
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/admin/admin-users.js